import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faXmark,
  faPaperclip,
  faCircleInfo,
  faSpinner,
  faFileInvoice,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { customerAuthorizationService } from '@/services/customer-authorization.service';
import { isNotFoundError } from '@/lib/is-not-found-error';
import { toast } from '@/lib/swal';
import type {
  CustomerQuotationRevisionStatus,
  CustomerResponseType,
} from '@/types/customer-authorization.types';
import {
  CustomerStatusBadge,
  type CustomerBadgeTone,
} from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerSkeletonModal } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { CustomerUnavailableState } from '@/components/customer-portal/CustomerUnavailableState';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { AUTHORIZATION_STATUS_TONE } from './customer-authorization-status-tone';

const RESPONSE_OPTIONS: { value: CustomerResponseType; label: string }[] = [
  { value: 'APPROVED', label: 'Approve' },
  { value: 'REJECTED', label: 'Reject' },
  { value: 'NEED_MORE_INFORMATION', label: 'Need More Information' },
  { value: 'ASK_QUESTION', label: 'Ask a Question' },
];

/**
 * Phase 7 Slice 5B (D-127 OD-3′, design §D.3) — offered ONLY when the server says a
 * quotation revision is awaiting this customer's decision (`quotation.awaitingDecision`).
 * It is never shown on a case without a quotation, nor after the customer has decided.
 */
const REQUEST_MODIFICATION_OPTION: { value: CustomerResponseType; label: string } = {
  value: 'REQUEST_MODIFICATION',
  label: 'Request Modification',
};

/** Customer-facing wording of the quotation revision status (never the raw enum). */
const QUOTATION_STATUS_VIEW: Record<
  CustomerQuotationRevisionStatus,
  { label: string; tone: CustomerBadgeTone }
> = {
  DRAFT: { label: 'In preparation', tone: 'neutral' },
  SENT: { label: 'Awaiting Your Decision', tone: 'warning' },
  ACCEPTED: { label: 'Accepted', tone: 'success' },
  REJECTED: { label: 'Declined', tone: 'danger' },
  MODIFICATION_REQUESTED: { label: 'Modification Requested', tone: 'info' },
};

interface CustomerAuthorizationCaseDetailProps {
  caseId: string;
  jobId: string;
  /** Whether the current customer user holds `customer-portal:authorizations:respond`.
   * Backend authorization remains authoritative — this only controls whether the
   * response controls render at all (never a disabled button a Staff/Read-Only
   * user could still see and be confused by). */
  canRespond: boolean;
  onClose: () => void;
}

/**
 * Customer Module Phase 6 (Authorization & Communication), improved UX Phase C
 * (2026-07-10) — context-before-decision layout, motion, and permission-gated
 * action visibility. Still a self-contained overlay rather than the shared
 * staff Modal component, for the same fixed-palette-isolation reason as
 * CustomerSearchBar/CustomerPagination.
 */
export function CustomerAuthorizationCaseDetail({
  caseId,
  jobId,
  canRespond,
  onClose,
}: CustomerAuthorizationCaseDetailProps) {
  const queryClient = useQueryClient();
  const [responseType, setResponseType] = useState<CustomerResponseType | null>(null);
  const [message, setMessage] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['customer-portal', 'authorization-case', caseId],
    queryFn: () => customerAuthorizationService.getById(caseId),
  });

  useEffect(() => {
    closeButtonRef.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const messageRequired = responseType !== null && responseType !== 'APPROVED';
  const showResponseControls = !!data?.actionRequired && canRespond;
  const quotation = data?.quotation ?? null;
  const responseOptions = quotation?.awaitingDecision
    ? [...RESPONSE_OPTIONS, REQUEST_MODIFICATION_OPTION]
    : RESPONSE_OPTIONS;
  /** Accept / Reject on a quotation are FINAL for the thread (D-128 Q-1). */
  const isFinalQuotationDecision =
    quotation !== null && (responseType === 'APPROVED' || responseType === 'REJECTED');

  async function handleDownloadQuotation() {
    setDownloading(true);
    try {
      await customerAuthorizationService.downloadQuotationDocument(caseId);
    } catch {
      toast.error('The quotation document could not be downloaded. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  async function handleSubmit() {
    if (!responseType) return;
    if (messageRequired && !message.trim()) {
      toast.error('Please add a comment before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await customerAuthorizationService.respond(caseId, {
        responseType,
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      toast.success('Your response has been submitted.');
      await queryClient.invalidateQueries({
        queryKey: ['customer-portal', 'authorization-case', caseId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['customer-portal', 'authorization-cases', jobId],
      });
      setResponseType(null);
      setMessage('');
      setConfirming(false);
    } catch {
      toast.error(
        'This case may already be resolved, or something went wrong. Please refresh and try again.',
      );
      await refetch();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-case-title"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="customer-modal-in relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-customer-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-customer-border px-6 py-4">
          <h2 id="auth-case-title" className="text-base font-semibold text-customer-text">
            {data ? data.caseNumber : 'Authorization Case'}
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-customer-text-secondary hover:bg-customer-bg hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          {isLoading && <CustomerSkeletonModal />}

          {/* A case can leave this contact's D-130 scope between the list load
                 and this click. The server then answers 404, and no amount of
                 retrying will change that — so it gets a way out, not a retry. */}
          {isError && !isLoading && isNotFoundError(error) && (
            <CustomerUnavailableState
              className="border-0 bg-transparent py-10 shadow-none"
              title="Case not available"
              description="This case either does not exist or is not available to your account."
              action={
                <CustomerButton variant="outline" onClick={onClose}>
                  Close
                </CustomerButton>
              }
            />
          )}

          {isError && !isLoading && !isNotFoundError(error) && (
            <div role="alert" className="flex flex-col items-center gap-3 py-10 text-center">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="h-6 w-6 text-customer-warning-text"
                aria-hidden="true"
              />
              <p className="text-sm text-customer-text">
                We couldn&apos;t load this case. Please check your connection and try again.
              </p>
              <CustomerButton variant="primary" onClick={() => void refetch()}>
                Try again
              </CustomerButton>
            </div>
          )}

          {data && !isLoading && !isError && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <CustomerStatusBadge
                  label={data.status}
                  tone={AUTHORIZATION_STATUS_TONE[data.status]}
                />
                {data.approvalRequestTypeName && (
                  <span className="text-sm text-customer-text-secondary">
                    {data.approvalRequestTypeName}
                  </span>
                )}
                {data.customApprovalName && (
                  <span className="text-sm text-customer-text-secondary">
                    {data.customApprovalName}
                  </span>
                )}
                {data.priorityName &&
                  (data.priorityColor ? (
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium"
                      // priorityColor is tenant-configured hex data (Priority master
                      // record), not a design-system token — the 2-digit alpha suffix
                      // only works with a real hex value, hence the branch below for
                      // the no-color case rather than a CSS-variable fallback here.
                      style={{
                        backgroundColor: `${data.priorityColor}20`,
                        color: data.priorityColor,
                      }}
                    >
                      {data.priorityName}
                    </span>
                  ) : (
                    <CustomerStatusBadge label={data.priorityName} tone="neutral" />
                  ))}
              </div>

              <div>
                <h3 className="mb-1 text-sm font-semibold text-customer-text">
                  Reason for Request
                </h3>
                <p className="text-sm text-customer-text/80">{data.reason}</p>
              </div>

              {data.evidence.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-customer-text">
                    Supporting Evidence
                  </h3>
                  <ul className="space-y-2">
                    {data.evidence.map((e, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-3 rounded-lg border border-customer-border p-2"
                      >
                        {e.thumbnailUrl ? (
                          <img
                            src={e.thumbnailUrl}
                            alt={e.label}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faPaperclip}
                            className="h-4 w-4 shrink-0 text-customer-text-secondary"
                          />
                        )}
                        {e.url ? (
                          <a
                            href={e.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-customer-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                          >
                            {e.label}
                          </a>
                        ) : (
                          <span className="text-sm text-customer-text">{e.label}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {quotation && (
                <div
                  data-testid="customer-quotation"
                  className="rounded-xl border border-customer-border p-4"
                >
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-customer-text">
                      <FontAwesomeIcon
                        icon={faFileInvoice}
                        className="h-3.5 w-3.5 text-customer-text-secondary"
                      />
                      Quotation
                    </h3>
                    <span className="text-xs text-customer-text-secondary">
                      Revision {quotation.revisionNumber}
                    </span>
                    <CustomerStatusBadge
                      label={QUOTATION_STATUS_VIEW[quotation.status].label}
                      tone={QUOTATION_STATUS_VIEW[quotation.status].tone}
                    />
                  </div>
                  <dl className="space-y-1 text-sm">
                    <div>
                      <dt className="inline text-customer-text-secondary">Reference: </dt>
                      <dd className="inline text-customer-text">
                        {quotation.quotationReference ?? '—'}
                      </dd>
                    </div>
                    {quotation.sentAt && (
                      <div>
                        <dt className="inline text-customer-text-secondary">Sent: </dt>
                        <dd className="inline text-customer-text">
                          {new Date(quotation.sentAt).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                  {quotation.hasDocument ? (
                    <button
                      type="button"
                      onClick={() => void handleDownloadQuotation()}
                      disabled={downloading}
                      className="mt-3 flex min-h-11 items-center gap-2 rounded-lg border border-customer-border px-3 text-sm font-medium text-customer-primary-text transition-colors hover:bg-customer-bg disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                    >
                      <FontAwesomeIcon
                        icon={downloading ? faSpinner : faDownload}
                        className={`h-3.5 w-3.5 ${downloading ? 'animate-spin' : ''}`}
                      />
                      Download quotation document
                      {quotation.documentName ? ` (${quotation.documentName})` : ''}
                    </button>
                  ) : (
                    <p className="mt-2 text-xs text-customer-text-secondary">
                      No document is attached to this revision.
                    </p>
                  )}
                  {(quotation.status === 'ACCEPTED' || quotation.status === 'REJECTED') && (
                    <p
                      data-testid="customer-quotation-final"
                      className="mt-3 text-xs text-customer-text-secondary"
                    >
                      You {quotation.status === 'ACCEPTED' ? 'accepted' : 'declined'} this
                      quotation. This decision is final — no further revision will be offered for
                      this request.
                    </p>
                  )}
                  {quotation.status === 'MODIFICATION_REQUESTED' && (
                    <p className="mt-3 text-xs text-customer-text-secondary">
                      You requested a modification. The service team is revising the quotation and
                      will send you a new revision to decide on.
                    </p>
                  )}
                </div>
              )}

              {data.yourResponses.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-customer-text">
                    Your Previous Responses
                  </h3>
                  <ul className="divide-y divide-customer-border">
                    {data.yourResponses.map((r) => (
                      <li key={r.id} className="py-2 text-sm">
                        <span className="font-medium text-customer-text">{r.responseType}</span>{' '}
                        <span className="text-customer-text-secondary">
                          {new Date(r.respondedAt).toLocaleString()}
                        </span>
                        <p className="mt-0.5 text-customer-text-secondary">{r.message}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.actionRequired && !canRespond && (
                <div className="rounded-lg border border-customer-border bg-customer-bg px-3 py-2 text-sm text-customer-text-secondary">
                  This case is awaiting a response from an account owner or manager at your company.
                  Your role does not include approval permissions.
                </div>
              )}

              {showResponseControls && (
                <div className="rounded-xl border border-customer-warning/40 bg-customer-warning/10 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-customer-text">Your Response</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {responseOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setResponseType(opt.value);
                          setConfirming(false);
                        }}
                        className={`min-h-11 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring ${
                          responseType === opt.value
                            ? 'border-customer-primary bg-customer-primary/10 text-customer-primary-text'
                            : 'border-customer-border bg-customer-surface text-customer-text hover:border-customer-primary/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {responseType && (
                    <div className="mt-3">
                      <p className="mb-2 flex items-start gap-2 text-xs text-customer-text-secondary">
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className="mt-0.5 h-3 w-3 shrink-0 text-customer-text-secondary"
                        />
                        {responseType === 'REQUEST_MODIFICATION'
                          ? 'Tell the service team what should change. They will revise the quotation and send you a new revision to decide on.'
                          : responseType === 'APPROVED'
                            ? 'You are approving this request. The service team will be notified and will proceed.'
                            : 'The service team will review your response and follow up accordingly.'}
                      </p>
                      {isFinalQuotationDecision && (
                        <p
                          data-testid="customer-quotation-final-warning"
                          className="mb-2 rounded-lg border border-customer-warning/40 bg-customer-surface px-3 py-2 text-xs text-customer-text"
                        >
                          {responseType === 'APPROVED' ? 'Accepting' : 'Declining'} is your final
                          decision on this quotation. No further revision can be requested or
                          offered afterwards.
                        </p>
                      )}
                      <label htmlFor="auth-response-message" className="sr-only">
                        Comment
                      </label>
                      <textarea
                        id="auth-response-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={
                          messageRequired ? 'Please explain (required)' : 'Add a comment (optional)'
                        }
                        rows={3}
                        className="w-full rounded-lg border border-customer-border p-2 text-sm text-customer-text focus:border-customer-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                      />
                      {!confirming ? (
                        <button
                          onClick={() => setConfirming(true)}
                          disabled={messageRequired && !message.trim()}
                          className="mt-2 min-h-11 rounded-lg bg-customer-primary px-4 text-sm font-medium text-customer-action-ink transition-colors hover:bg-customer-primary/90 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-surface"
                        >
                          Continue
                        </button>
                      ) : (
                        <div className="customer-modal-in mt-2 flex flex-col gap-2 rounded-lg bg-customer-surface p-3 sm:flex-row sm:items-center">
                          <p className="flex-1 text-sm text-customer-text">
                            Submit this response? This cannot be undone.
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setConfirming(false)}
                              className="min-h-11 rounded-lg border border-customer-border px-3 text-sm text-customer-text hover:bg-customer-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => void handleSubmit()}
                              disabled={submitting}
                              className="flex min-h-11 items-center gap-1.5 rounded-lg bg-customer-primary px-3 text-sm font-medium text-customer-action-ink hover:bg-customer-primary/90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                            >
                              {submitting && (
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  className="h-3 w-3 animate-spin"
                                />
                              )}
                              Confirm
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
